import { useCallback, useEffect, useState } from "react";
import type { SessionResponse } from "@kidtube/shared";
import { api, ApiClientError } from "./api";
import { AppShell, type AppPage } from "./components/AppShell";
import { LoadingPage, Toast } from "./components/Feedback";
import { LoginScreen } from "./components/LoginScreen";
import { BlocksScreen } from "./screens/BlocksScreen";
import { DashboardScreen } from "./screens/DashboardScreen";
import { HistoryScreen } from "./screens/HistoryScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { SearchScreen } from "./screens/SearchScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { errorMessage } from "./utils";

interface Notice {
  message: string;
  tone: "success" | "error";
}

export function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [page, setPage] = useState<AppPage>("dashboard");
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    let active = true;
    api
      .session()
      .then((current) => {
        if (active) setSession(current);
      })
      .catch((error: unknown) => {
        if (active && (!(error instanceof ApiClientError) || error.status !== 401)) {
          setLoginError(errorMessage(error));
        }
      })
      .finally(() => {
        if (active) setCheckingSession(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const showSuccess = useCallback((message: string) => {
    setNotice({ message, tone: "success" });
  }, []);

  const showError = useCallback((error: unknown) => {
    setNotice({ message: errorMessage(error), tone: "error" });
  }, []);

  async function login(email: string, password: string) {
    setBusy(true);
    setLoginError(null);
    try {
      setSession(await api.login(email, password));
    } catch (error) {
      setLoginError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    setBusy(true);
    try {
      await api.logout();
      setSession(null);
      setPage("dashboard");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  }

  if (checkingSession) return <LoadingPage />;
  if (session === null) return <LoginScreen busy={busy} error={loginError} onLogin={login} />;

  const commonProps = { showSuccess, showError };
  return (
    <AppShell
      page={page}
      email={session.parent.email}
      busy={busy}
      onNavigate={setPage}
      onLogout={logout}
    >
      {page === "dashboard" && <DashboardScreen {...commonProps} onNavigate={setPage} />}
      {page === "search" && <SearchScreen {...commonProps} />}
      {page === "home" && <HomeScreen {...commonProps} />}
      {page === "blocks" && <BlocksScreen {...commonProps} />}
      {page === "history" && <HistoryScreen {...commonProps} />}
      {page === "settings" && <SettingsScreen {...commonProps} />}
      <Toast
        message={notice?.message ?? null}
        tone={notice?.tone ?? "success"}
        onDismiss={() => setNotice(null)}
      />
    </AppShell>
  );
}
